import { useState,useCallback } from 'react'
import { api } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { HoverCard,HoverCardContent,HoverCardTrigger } from '@/components/ui/hover-card'
import { Shield,Loader2,ShieldAlert,ShieldCheck,Clock,User } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'

interface HoldEvent {
  id: string
  action: string
  reason?: string | null
  note?: string | null
  createdAt: string
  user?: { id: string; name?: string; email?: string } | null
}

interface HoldAuditHoverCardProps {
  packageId: string
  label?: string
}

function isPlaced(action: string) {
  return action.toLowerCase() === 'placed'
}

export function HoldAuditHoverCard({ packageId, label = 'Hold' }: HoldAuditHoverCardProps) {
  const [events, setEvents] = useState<HoldEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchHistory = useCallback(async () => {
    if (fetched) return
    setLoading(true)
    try {
      const res = await api.get<{ data?: unknown; items?: unknown }>(`/metrc/packages/${packageId}/hold/history`,
        { params: { limit: 10 } },
      )
      const rawEvents: unknown = res?.data ?? res?.items ?? res ?? []
      setEvents(Array.isArray(rawEvents) ? (rawEvents as HoldEvent[]) : [])
      setFetched(true)
    } catch (err: unknown) {
      console.error('Failed to fetch hold history:', err)
    } finally {
      setLoading(false)
    }
  }, [packageId, fetched])

  return (
    <HoverCard openDelay={300} closeDelay={100} onOpenChange={(open) => { if (open) void fetchHistory() }}>
      <HoverCardTrigger asChild>
        <Badge
          variant="secondary"
          className="text-[10px] h-5 px-1.5 font-normal gap-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 cursor-help"
        >
          <Shield className="w-3 h-3" />
          {label}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" align="start" side="bottom">
        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
          <div className="flex items-center justify-center h-6 w-6 rounded-md bg-amber-500/10">
            <Shield className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground leading-none">Hold History</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Audit trail for this package</p>
          </div>
        </div>
        <Separator />

        {loading && !fetched ? (
          <div className="flex flex-col items-center gap-1.5 py-6 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-[11px]">Loading history…</span>
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 py-6 text-muted-foreground">
            <Shield className="w-4 h-4 opacity-40" />
            <span className="text-[11px]">No hold events recorded</span>
          </div>
        ) : (
          <div className="max-h-[260px] overflow-y-auto">
            {events.map((evt, idx) => {
              const placed = isPlaced(evt.action)
              return (
                <div key={evt.id}>
                  <div className="px-3 py-2 flex gap-2.5">
                    <div
                      className={cn(
                        'mt-0.5 flex items-center justify-center h-5 w-5 rounded-full shrink-0',
                        placed
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
                      )}
                    >
                      {placed ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'text-[11px] font-medium',
                            placed
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-emerald-600 dark:text-emerald-400',
                          )}
                        >
                          {placed ? 'Hold Placed' : 'Hold Released'}
                        </span>
                        {evt.createdAt && (
                          <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {formatDistanceToNow(new Date(evt.createdAt), { addSuffix: true })}
                          </span>
                        )}
                      </div>

                      {(evt.reason ?? evt.note) && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                          {evt.reason ?? evt.note}
                        </p>
                      )}

                      {evt.user?.name && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground/60">
                          <User className="h-2.5 w-2.5" />
                          <span>{evt.user.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {idx < events.length - 1 && <Separator />}
                </div>
              )
            })}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
